import type { PageNode, WebsiteDefinition } from "@baze/schema";
import { SCHEMA_DAYS, eventIsoDate, formatRupiah, imageSrc, safeHref, safeImageSrc, socialLinks, sortedHours, type ImageLike } from "./helpers";

export interface SeoImage {
  url: string;
  width?: number;
  height?: number;
  alt?: string;
}

export interface SeoResult {
  title: string;
  description: string;
  canonical: string;
  robots: { index: boolean; follow: boolean };
  openGraph: {
    type: "website";
    url: string;
    title: string;
    description: string;
    siteName: string;
    locale: string;
    images: SeoImage[];
  };
  twitter: {
    card: "summary" | "summary_large_image";
    title: string;
    description: string;
    images: string[];
  };
  /** The site's main node on its own (LocalBusiness subtype, Organization, Person or Event; also part of `graph`). */
  jsonLd: Record<string, unknown>;
  /**
   * The page's full JSON-LD document: business + WebSite + WebPage, plus BreadcrumbList on sub-pages and
   * FAQPage when the page shows a FAQ. Render with <JsonLd data={seo.graph} />.
   */
  graph: Record<string, unknown>;
}

export interface BuildSeoOptions {
  /** Public origin of the site, e.g. "https://kopi-senja.example.com". */
  canonicalOrigin: string;
  /**
   * schema.org type from the industry (catalog industries[].schemaType): a LocalBusiness subtype, a place
   * (PlaceOfWorship), an organization (Organization, MusicGroup, EducationalOrganization …), Person, or
   * Event for invitations. Each kind only gets the properties schema.org defines for it.
   */
  schemaType?: string;
  /** Price range ("Rp 20.000 – Rp 60.000"); derived from the site's listed prices when omitted. */
  priceRange?: string;
  /** Absolute image used when the page and its hero have none (e.g. a generated share card); beats the logo. */
  fallbackImage?: SeoImage;
}

/** Search engines show ~155–160 characters; cut at a word boundary with an ellipsis. */
export function clampText(value: string, max = 160): string {
  const t = value.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:–-]+$/, "")}…`;
}

function originOf(value: string): string {
  try {
    const u = new URL(value);
    if (u.protocol !== "https:" && u.protocol !== "http:") return "";
    return u.origin;
  } catch {
    return "";
  }
}

function absolute(origin: string, src: string): string {
  return src.startsWith("/") ? `${origin}${src}` : src;
}

function ogImage(origin: string, image: ImageLike | undefined): SeoImage | null {
  if (!image || !safeImageSrc(image.src)) return null;
  const src = imageSrc(image, 1600);
  if (!src) return null;
  return {
    url: absolute(origin, src),
    ...(image.width ? { width: Math.min(image.width, 1600) } : {}),
    ...(image.width && image.height ? { height: Math.round((image.height * Math.min(image.width, 1600)) / image.width) } : {}),
    ...(image.alt ? { alt: image.alt } : {}),
  };
}

function firstHeroImage(page: PageNode): ImageLike | undefined {
  for (const s of page.sections) if (s.type === "hero" && !s.hidden && s.content.image) return s.content.image;
  return undefined;
}

/** First visible hero/about copy on the page: the description fallback when the page has no SEO text. */
function pageSummary(page: PageNode): string {
  for (const s of page.sections) {
    if (s.hidden) continue;
    if (s.type === "hero" && s.content.description?.trim()) return s.content.description;
    if (s.type === "about" && s.content.body.trim()) return s.content.body;
  }
  return "";
}

/** Prices listed on visible menu, product and service sections across the whole site. */
function listedPrices(definition: WebsiteDefinition): number[] {
  const prices: number[] = [];
  for (const page of definition.pages)
    for (const s of page.sections) {
      if (s.hidden) continue;
      if (s.type === "menu") for (const c of s.content.categories) for (const i of c.items) prices.push(i.price ?? 0);
      else if (s.type === "products" || s.type === "services") for (const i of s.content.items) prices.push(i.price ?? 0);
    }
  return prices.filter((p) => p > 0);
}

/** "Rp 20.000 – Rp 60.000" from the listed prices, or undefined when there are none. */
export function derivePriceRange(definition: WebsiteDefinition): string | undefined {
  const prices = listedPrices(definition);
  if (!prices.length) return undefined;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatRupiah(min) : `${formatRupiah(min)} – ${formatRupiah(max)}`;
}

function faqPage(page: PageNode, url: string): Record<string, unknown> | null {
  const items = page.sections.flatMap((s) => (s.type === "faq" && !s.hidden ? s.content.items : []));
  if (!items.length) return null;
  return {
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

/** Group days with identical hours into OpeningHoursSpecification entries (closed days omitted). */
export function openingHoursSpecification(hours: WebsiteDefinition["site"]["hours"]): Array<Record<string, unknown>> {
  const groups = new Map<string, string[]>();
  for (const h of sortedHours(hours)) {
    if (h.closed || !h.open || !h.close) continue;
    const key = `${h.open}-${h.close}`;
    groups.set(key, [...(groups.get(key) ?? []), SCHEMA_DAYS[h.day]]);
  }
  return [...groups.entries()].map(([key, days]) => {
    const [opens, closes] = key.split("-");
    return { "@type": "OpeningHoursSpecification", dayOfWeek: days, opens, closes };
  });
}

const SCHEMA_TYPE = /^[A-Za-z]{2,60}$/;

/** Types that are not a LocalBusiness: no prices, and (except places) no opening hours, geo or map. */
const ORGANIZATIONS = new Set(["Organization", "MusicGroup", "PerformingGroup", "EducationalOrganization", "NGO", "Corporation"]);
const PLACES = new Set(["PlaceOfWorship", "Place", "CivicStructure"]);

type Kind = "business" | "place" | "organization" | "person" | "event";

function kindOf(type: string): Kind {
  if (type === "Person") return "person";
  if (type === "Event") return "event";
  if (ORGANIZATIONS.has(type)) return "organization";
  if (PLACES.has(type)) return "place";
  return "business";
}

/** When and where an invitation happens: the first visible countdown, else the first dated event. */
function eventFacts(definition: WebsiteDefinition): { startDate: string; endDate?: string; venue?: string; address?: string } | null {
  for (const page of definition.pages)
    for (const s of page.sections) {
      if (s.hidden) continue;
      if (s.type === "countdown") {
        const startDate = eventIsoDate(s.content.date, s.content.time, s.content.timeNote);
        if (startDate) return { startDate };
      }
    }
  for (const page of definition.pages)
    for (const s of page.sections) {
      if (s.hidden || s.type !== "events") continue;
      for (const e of s.content.items) {
        const startDate = eventIsoDate(e.date, e.startTime, e.timeNote);
        if (!startDate) continue;
        const endDate = e.endTime ? eventIsoDate(e.date, e.endTime, e.timeNote) ?? undefined : undefined;
        return { startDate, ...(endDate ? { endDate } : {}), ...(e.venue ? { venue: e.venue } : {}), ...(e.address ? { address: e.address } : {}) };
      }
    }
  return null;
}

/** Page metadata + LocalBusiness JSON-LD. Framework-agnostic; map onto Next `Metadata` in apps/web. */
export function buildSeo(definition: WebsiteDefinition, page: PageNode, opts: BuildSeoOptions): SeoResult {
  const site = definition.site;
  const origin = originOf(opts.canonicalOrigin);
  const isHome = page.slug === "/";
  const c = site.contact;
  const lang = site.locale;
  const title =
    page.seo.title.trim() ||
    (isHome
      ? clampText(site.tagline ? `${site.name} – ${site.tagline}` : c.city ? `${site.name} – ${c.city}` : site.name, 70)
      : clampText(`${page.title} – ${site.name}`, 70));
  const description = clampText(
    page.seo.description.trim() || (isHome ? site.tagline || pageSummary(page) : pageSummary(page) || site.tagline) || "",
  );
  const canonical = `${origin}${isHome ? "/" : page.slug}`;
  // A generated share card beats the logo: a square logo stretched into a large card looks broken.
  const image =
    ogImage(origin, page.seo.ogImage) ??
    ogImage(origin, firstHeroImage(page)) ??
    opts.fallbackImage ??
    ogImage(origin, site.logo) ??
    null;
  const images = image ? [image] : [];

  const telephone = c.phone ?? (c.whatsapp ? `+${c.whatsapp}` : undefined);
  const sameAs = socialLinks(site).map((s) => s.href);
  const hours = openingHoursSpecification(site.hours);
  const address =
    c.address || c.city
      ? {
          "@type": "PostalAddress",
          ...(c.address ? { streetAddress: c.address } : {}),
          ...(c.city ? { addressLocality: c.city } : {}),
          addressCountry: "ID",
        }
      : undefined;
  const logo = site.logo ? ogImage(origin, site.logo) : null;
  const priceRange = opts.priceRange ?? derivePriceRange(definition);
  const mapsUrl = safeHref(c.mapsUrl);
  const businessId = `${origin}/#business`;
  const websiteId = `${origin}/#website`;

  let type = opts.schemaType && SCHEMA_TYPE.test(opts.schemaType) ? opts.schemaType : "LocalBusiness";
  // An invitation without a date cannot be a valid Event; describe its hosts as an organization instead.
  const when = type === "Event" ? eventFacts(definition) : null;
  if (type === "Event" && !when) type = "Organization";
  const kind = kindOf(type);
  const local = kind === "business" || kind === "place";
  const geo = c.geo ? { "@type": "GeoCoordinates", latitude: c.geo.lat, longitude: c.geo.lng } : undefined;

  const business: Record<string, unknown> =
    kind === "event" && when
      ? {
          "@type": "Event",
          "@id": businessId,
          name: site.name,
          ...(site.tagline ? { description: site.tagline } : {}),
          url: `${origin}/`,
          ...(images.length ? { image: images.map((i) => i.url) } : {}),
          startDate: when.startDate,
          ...(when.endDate ? { endDate: when.endDate } : {}),
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          ...(when.venue || address || geo
            ? {
                location: {
                  "@type": "Place",
                  name: when.venue ?? c.city ?? site.name,
                  ...(when.address ? { address: when.address } : address ? { address } : {}),
                  ...(geo ? { geo } : {}),
                },
              }
            : {}),
        }
      : {
          "@type": type,
          "@id": businessId,
          name: site.name,
          ...(site.tagline ? { description: site.tagline } : {}),
          url: `${origin}/`,
          ...(images.length ? { image: images.map((i) => i.url) } : {}),
          ...(logo && kind !== "person" && kind !== "place" ? { logo: logo.url } : {}),
          ...(telephone ? { telephone } : {}),
          ...(c.email ? { email: c.email } : {}),
          ...(address ? { address } : {}),
          ...(local && geo ? { geo } : {}),
          ...(local && mapsUrl && /^https?:/.test(mapsUrl) ? { hasMap: mapsUrl } : {}),
          ...(c.city && kind !== "person" && kind !== "place" ? { areaServed: { "@type": "City", name: c.city } } : {}),
          ...(local && hours.length ? { openingHoursSpecification: hours } : {}),
          ...(sameAs.length ? { sameAs } : {}),
          ...(kind === "business" && priceRange ? { priceRange, currenciesAccepted: "IDR" } : {}),
        };

  const jsonLd = { "@context": "https://schema.org", ...business };
  const website = {
    "@type": "WebSite",
    "@id": websiteId,
    url: `${origin}/`,
    name: site.name,
    ...(site.tagline ? { description: site.tagline } : {}),
    inLanguage: lang,
    // publisher must be an Organization or Person; an Event or a place is only what the site is about.
    ...(kind === "event" || kind === "place" ? {} : { publisher: { "@id": businessId } }),
  };
  const breadcrumb = isHome
    ? null
    : {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: site.name, item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: page.title, item: canonical },
        ],
      };
  const webPage = {
    "@type": "WebPage",
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: title,
    ...(description ? { description } : {}),
    inLanguage: lang,
    isPartOf: { "@id": websiteId },
    about: { "@id": businessId },
    ...(images.length ? { primaryImageOfPage: { "@type": "ImageObject", url: images[0]!.url } } : {}),
    ...(breadcrumb ? { breadcrumb: { "@id": breadcrumb["@id"] } } : {}),
  };
  const faq = faqPage(page, canonical);
  const graph = {
    "@context": "https://schema.org",
    "@graph": [business, website, webPage, ...(breadcrumb ? [breadcrumb] : []), ...(faq ? [faq] : [])],
  };

  return {
    title,
    description,
    canonical,
    robots: { index: !page.seo.noindex, follow: true },
    openGraph: {
      type: "website",
      url: canonical,
      title,
      description,
      siteName: site.name,
      locale: site.locale === "en-US" ? "en_US" : "id_ID",
      images,
    },
    twitter: {
      card: images.length ? "summary_large_image" : "summary",
      title,
      description,
      images: images.map((i) => i.url),
    },
    jsonLd,
    graph,
  };
}

/** JSON for a <script type="application/ld+json">: `<`, `>`, `&` and line separators escaped. */
export function serializeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/[\u2028\u2029]/g, (ch) => (ch === "\u2028" ? "\\u2028" : "\\u2029"));
}

/** The engine's only dangerouslySetInnerHTML: JSON-LD, serialized with HTML-significant chars escaped. */
export function JsonLd({ data }: { data: unknown }) {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(data) }} />;
}
